import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PartnerModalConatiner from '../../../containers/common/form/modal/PartnerModalConatiner';
import NumberFormatText from '../../common/form/NumberFormatText';
import { getNumberComma } from '../../../utils/numberUtils';
import {
  computeRowAmounts,
  panelFieldSx,
  PriceNumberFormat,
  profitRateColor,
  validateCostRow,
} from './costCalcShared';

const SlideUpTransition = React.forwardRef(function SlideUpTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const buildDraft = (cost) => ({
  productNm: cost?.productNm ?? '',
  productDesc: cost?.productDesc ?? '',
  masterTyp: cost?.masterTyp,
  itemCd: cost?.itemCd ?? '',
  expCnt: cost?.expCnt ?? '',
  productUnit: cost?.productUnit ?? '',
  unitVal: cost?.unitVal ?? '',
  priceVal: cost?.priceVal ?? '',
  sellPartnerUuid: cost?.sellPartnerUuid ?? '',
  sellPartnerNm: cost?.sellPartnerNm ?? '',
});

const Field = ({ label, required, error, children }) => (
  <Box sx={{ mt: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
      {label}
      {required && (
        <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
          * 필수
        </Typography>
      )}
    </Typography>
    {children}
    {error && (
      <Typography variant="caption" color="error.main" component="div" sx={{ mt: 0.25 }}>
        {error}
      </Typography>
    )}
  </Box>
);

// 옵션 B(2026-10-27 owner 승인) 우측 패널 - 표 행을 클릭하면 열리고, 이 안에서 고친 값은
// "적용"/"다음"/"이전"을 눌러야 formik(폼 상태)에 반영된다(✕/ESC는 반영 없이 닫기만).
// 600px 미만에서는 같은 내용을 화면 하단 바텀시트(Dialog + Slide up)로 보여준다.
const CostCalcEditPanel = ({
  cost,
  index,
  count,
  itemCdOptions,
  onApply,
  onNext,
  onPrev,
  onRequestDelete,
  onClose,
  isMobile,
}) => {
  const [draft, setDraft] = useState(() => buildDraft(cost));
  const [partnerOpen, setPartnerOpen] = useState(false);
  const partnerFieldRef = useRef(null);

  // 행을 바꿔가며 열 때(다음/이전, 또는 다른 행 클릭) 그 행의 "방금 적용된" 값으로 다시 시작한다.
  useEffect(() => {
    setDraft(buildDraft(cost));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const isEtc = draft.masterTyp !== 'N';
  const errors = validateCostRow(draft);
  const { saleVal, buyVal, profit, profitRate } = computeRowAmounts(draft);

  const setField = (name) => (e) => setDraft((d) => ({ ...d, [name]: e.target.value }));

  const handlePartnerSelected = (partner) => {
    setDraft((d) => ({ ...d, sellPartnerUuid: partner.partnerUuid, sellPartnerNm: partner.partnerNm }));
    setPartnerOpen(false);
  };

  const handleApplyClick = () => onApply(draft);
  const handleNextClick = () => onNext(draft);
  const handlePrevClick = () => onPrev(draft);

  const handlePanelKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    // 패널은 container의 <form onSubmit=.../> 안에 있다 - Enter가 폼 제출로 새는 것부터 막는다.
    e.preventDefault();
    if (document.activeElement === partnerFieldRef.current) {
      handleApplyClick();
    }
  };

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
      onKeyDown={handlePanelKeyDown}
    >
      <Box
        sx={{
          px: 2.25,
          py: 1.75,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          품목 {index + 1} 수정
        </Typography>
        <IconButton aria-label="닫기" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2.25, py: 1.5, flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Field label="품목명">
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            value={draft.productNm}
            onChange={setField('productNm')}
            placeholder="매출내용(제품 또는 용역명)"
            sx={panelFieldSx}
          />
        </Field>

        <Field label="설명">
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            value={draft.productDesc}
            onChange={setField('productDesc')}
            placeholder="설명(선택)"
            sx={panelFieldSx}
          />
        </Field>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              구분
            </Typography>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              disabled
              value={isEtc ? '기타 항목' : '제품'}
              sx={panelFieldSx}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
              아이템
            </Typography>
            {isEtc ? (
              <FormControl size="small" fullWidth sx={panelFieldSx}>
                <Select value={draft.itemCd} onChange={setField('itemCd')} displayEmpty>
                  {(itemCdOptions || []).map((option) => (
                    <MenuItem key={option.revVal2} value={option.revVal2}>
                      {option.revVal1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField size="small" fullWidth variant="outlined" disabled value={draft.itemCd} sx={panelFieldSx} />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
          <Field label="수량" error={errors.expCnt}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              value={draft.expCnt}
              onChange={setField('expCnt')}
              error={Boolean(errors.expCnt)}
              InputProps={{ inputComponent: NumberFormatText }}
              sx={panelFieldSx}
            />
          </Field>
          <Field label="단위">
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              value={draft.productUnit}
              onChange={setField('productUnit')}
              sx={panelFieldSx}
            />
          </Field>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1.5 }}>
          <Field label="매출 단가" error={errors.unitVal}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              value={draft.unitVal}
              onChange={setField('unitVal')}
              error={Boolean(errors.unitVal)}
              inputProps={{ isNegative: true }}
              InputProps={{ inputComponent: PriceNumberFormat }}
              sx={panelFieldSx}
            />
          </Field>
          <Field label="매입 단가" error={errors.priceVal}>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              value={draft.priceVal}
              onChange={setField('priceVal')}
              error={Boolean(errors.priceVal)}
              inputProps={{ isNegative: true }}
              InputProps={{ inputComponent: PriceNumberFormat }}
              sx={panelFieldSx}
            />
          </Field>
        </Box>

        <Field label="매입처" required error={errors.sellPartnerNm}>
          <Box
            ref={partnerFieldRef}
            role="button"
            tabIndex={0}
            onClick={() => setPartnerOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: 1,
              borderColor: errors.sellPartnerNm ? 'error.main' : 'divider',
              borderRadius: 1,
              px: 1.25,
              py: 0.875,
              cursor: 'pointer',
              bgcolor: errors.sellPartnerNm ? 'error.lighter' : 'transparent',
              '&:hover': { borderColor: errors.sellPartnerNm ? 'error.main' : 'text.secondary' },
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: draft.sellPartnerNm ? 'text.primary' : 'text.disabled', flex: 1 }}
            >
              {draft.sellPartnerNm || '거래처 검색…'}
            </Typography>
          </Box>
        </Field>

        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.25 }}>
            <Typography variant="caption" color="text.secondary">매출 금액</Typography>
            <Typography variant="body2" fontWeight={700}>{getNumberComma(saleVal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.25 }}>
            <Typography variant="caption" color="text.secondary">매입 금액</Typography>
            <Typography variant="body2" fontWeight={700}>{getNumberComma(buyVal)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.25 }}>
            <Typography variant="caption" color="text.secondary">이익 / 이익률</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ color: profitRateColor(profitRate) }}>
              {getNumberComma(profit)} · {getNumberComma(profitRate)}%
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          px: 2.25,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          gap: 1,
        }}
      >
        <Button variant="outlined" color="error" size="small" onClick={onRequestDelete}>
          품목 삭제
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" color="inherit" disabled={index <= 0} onClick={handlePrevClick}>
            ◀ 이전
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            disabled={index >= count - 1}
            onClick={handleNextClick}
          >
            다음 ▶
          </Button>
          <Button variant="contained" size="small" onClick={handleApplyClick}>
            적용
          </Button>
        </Box>
      </Box>

      <PartnerModalConatiner
        link
        title="매입처 선택"
        open={partnerOpen}
        handleClose={() => setPartnerOpen(false)}
        selectedPartner={handlePartnerSelected}
      />
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog
        open
        onClose={onClose}
        fullWidth
        TransitionComponent={SlideUpTransition}
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            m: 0,
            width: '100%',
            maxHeight: '85vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>{content}</Box>
      </Dialog>
    );
  }

  return (
    <Box
      sx={{
        width: 380,
        flexShrink: 0,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        maxHeight: '80vh',
        boxShadow: (theme) => theme.shadows[2],
      }}
    >
      {content}
    </Box>
  );
};

export default CostCalcEditPanel;
